import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, QuickPayOption } from '../lib/api'

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  PAID: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

const QP_LABELS: Record<string, { label: string; color: string }> = {
  INSTANT:    { label: 'Same-Day Payout',    color: 'bg-green-50 border-green-300' },
  THREE_DAY:  { label: '3-Day Payout',       color: 'bg-blue-50 border-blue-300' },
  THIRTY_DAY: { label: 'Standard 30-Day',    color: 'bg-gray-50 border-gray-200' },
}

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const navigate = useNavigate()
  const [selectedQP, setSelectedQP] = useState<string | null>(null)
  const [qpSubmitted, setQpSubmitted] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: async () => {
      const r = await api.invoices.get(invoiceId!)
      return r.data.invoice
    },
    enabled: !!invoiceId,
  })

  const handleQPSelect = (type: string) => {
    setSelectedQP(type)
    setQpSubmitted(true)
    // In demo: show confirmation. Real Stripe payout deferred.
  }

  if (isLoading) return <div className="container mx-auto p-6 text-gray-400">Loading…</div>
  if (!data) return <div className="container mx-auto p-6 text-red-600">Invoice not found.</div>

  const invoice = data as any // raw DB row may have different field names
  const amount = typeof invoice.amount === 'number' ? invoice.amount : parseFloat(invoice.amount ?? '0')
  const invoiceNumber = invoice.invoice_number ?? invoice.invoiceNumber ?? invoice.id?.slice(0, 8)
  const status = invoice.status ?? 'PENDING'
  const dueDate = invoice.due_date ?? invoice.dueDate
  const jobId = invoice.job_id ?? invoice.jobId ?? null
  const quickPayOptions: QuickPayOption[] = invoice.quickPayOptions ?? invoice.quick_pay_options ?? [
    { type: 'INSTANT',    feeBps: 350, label: 'Same-day payout',  feeAmount: +(amount * 0.035).toFixed(2), netAmount: +(amount * 0.965).toFixed(2) },
    { type: 'THREE_DAY',  feeBps: 200, label: '3-business-day',   feeAmount: +(amount * 0.02).toFixed(2),  netAmount: +(amount * 0.98).toFixed(2) },
    { type: 'THIRTY_DAY', feeBps: 0,   label: 'Standard 30-day',  feeAmount: 0,                            netAmount: amount },
  ]

  const { data: job } = useQuery({
    queryKey: ['invoice-job', jobId],
    queryFn: async () => (await api.jobs.get(jobId!)).data,
    enabled: !!jobId,
  })
  const { data: load } = useQuery({
    queryKey: ['invoice-load', job?.loadId],
    queryFn: async () => (await api.loads.get(job!.loadId)).data,
    enabled: !!job?.loadId,
  })
  const podUploaded = Boolean(job?.proofOfDeliveryUrl)

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/invoices')} className="text-gray-400 hover:text-gray-700 text-sm">← Invoices</button>
        <h1 className="text-xl font-bold text-gray-900 font-mono">{invoiceNumber}</h1>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[status] ?? 'bg-gray-100'}`}>
          {status}
        </span>
      </div>

      {/* Amount */}
      <div className="bg-white rounded-xl border p-5 mb-4">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-gray-500 mb-1">Invoice Amount</div>
            <div className="text-4xl font-bold text-gray-900">${amount.toLocaleString()}</div>
            <div className="text-sm text-gray-400 mt-1">
              {invoice.currency ?? 'USD'}
              {dueDate && ` · Due ${new Date(dueDate).toLocaleDateString()}`}
            </div>
          </div>
          <div className="text-right text-sm text-gray-500">
            {jobId ? (
              <div>Job: <span className="font-mono">{jobId.slice(0, 8)}</span></div>
            ) : null}
            <div className="text-xs text-gray-400 mt-1">{new Date(invoice.created_at ?? invoice.createdAt).toLocaleDateString()}</div>
          </div>
        </div>
      </div>

      {jobId && (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <h2 className="font-semibold text-gray-800 mb-2">POD Status</h2>
          <div className={`text-sm font-medium ${podUploaded ? 'text-green-700' : 'text-amber-700'}`}>
            {podUploaded ? 'POD: Uploaded' : 'POD: Missing'}
          </div>
          {job?.proofOfDeliveryFilename && <div className="text-xs text-gray-500 mt-1">{job.proofOfDeliveryFilename}</div>}
          {job?.proofOfDeliveryUploadedAt && <div className="text-xs text-gray-400 mt-1">Uploaded {new Date(job.proofOfDeliveryUploadedAt).toLocaleString()}</div>}
          {podUploaded && job?.proofOfDeliveryUrl && (
            <a href={job.proofOfDeliveryUrl} className="inline-block mt-2 text-sm text-blue-700 underline">Open POD</a>
          )}
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            {load?.id && <Link to={`/loads/${load.id}/rate-confirmation`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Rate Confirmation</Link>}
            <Link to={`/jobs/${jobId}/dispatch-sheet`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">Dispatch Sheet</Link>
            <Link to={`/jobs/${jobId}/bol`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">BOL</Link>
            <Link to={`/jobs/${jobId}/pod`} className="rounded-lg border border-gray-300 px-3 py-2 text-gray-700 hover:bg-gray-50">POD</Link>
          </div>
        </div>
      )}

      {/* QuickPay */}
      {!qpSubmitted ? (
        <div className="bg-white rounded-xl border p-5 mb-4">
          <h2 className="font-semibold text-gray-800 mb-1">QuickPay Options</h2>
          <p className="text-xs text-gray-400 mb-4">Select a payout speed. Fee deducted from invoice amount.</p>
          <div className="space-y-3">
            {quickPayOptions.map(opt => {
              const meta = QP_LABELS[opt.type] ?? { label: opt.type, color: 'bg-gray-50 border-gray-200' }
              return (
                <div key={opt.type} className={`border-2 rounded-xl p-4 flex justify-between items-center ${meta.color}`}>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{meta.label}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {opt.feeBps > 0 ? `${(opt.feeBps / 100).toFixed(1)}% fee — $${opt.feeAmount.toLocaleString()} deducted` : 'No fee'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">${opt.netAmount.toLocaleString()}</div>
                    <button onClick={() => handleQPSelect(opt.type)}
                      className="mt-1 text-xs bg-blue-700 text-white px-3 py-1 rounded-lg hover:bg-blue-800">
                      Select
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
            ⚠ Demo / Internal Payment Record — No real bank transfer occurs until Stripe live mode is configured.
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-4 text-center">
          <div className="text-3xl mb-2">✅</div>
          <p className="font-bold text-green-800">QuickPay Selected: {selectedQP}</p>
          <p className="text-sm text-green-700 mt-1">Payout initiated — status: PAYOUT_PENDING (ACH)</p>
          <p className="text-xs text-green-600 mt-2">
            Demo mode — no real transfer. Contact AUXpay team to connect your bank account.
          </p>
        </div>
      )}

      <button onClick={() => navigate('/invoices')}
        className="text-sm text-gray-500 hover:text-gray-700 underline">← Back to Invoices</button>
    </div>
  )
}
